import $ from "jquery";
import Swiper from "swiper";
import { Navigation, Pagination, Scrollbar, Autoplay, Thumbs, EffectCoverflow, EffectFade } from "swiper/modules";
Swiper.use([Navigation, Pagination, Scrollbar, Autoplay, Thumbs, EffectCoverflow, EffectFade]);
window.Swiper = Swiper;

import "swiper/css/bundle";

// Define slider block configurations - single source of truth
const SLIDER_CONFIG = {
    ".ms_content-carousel": { main: ".swiper", thumb: null, selector: "swiper" },
    ".ms_carousel": { main: ".carousel", thumb: ".thumbnail-carousel", selector: "carousel" },
    ".ms_cards": [
        { main: ".card-slider", thumb: null, selector: "card-slider" },
        { main: ".card-mobile-slider", thumb: null, selector: "card-mobile-slider" },
    ],
    ".ms_products": [
        { main: ".product-slider", thumb: null, selector: "product-slider" },
        { main: ".product-mobile-slider", thumb: null, selector: "product-mobile-slider" },
    ],
    ".ms_testimonial-quote": { main: ".item-slider", thumb: null, selector: "item-slider" },
    ".ms_usps": { main: ".usps-slider", thumb: null, selector: "usps-slider" },
};

// Generate selectors from config
const SLIDER_SELECTORS = {
    containers: Object.keys(SLIDER_CONFIG).join(", "),
    swipers: Object.values(SLIDER_CONFIG)
        .flatMap((cfg) => (Array.isArray(cfg) ? cfg : [cfg]))
        .flatMap((cfg) => [cfg.main, cfg.thumb].filter(Boolean))
        .join(", "),
};

const initializeSliders = function (sliders) {
    // Helper to parse options from data attribute
    const parseOptions = (el) => (el ? JSON.parse(el.dataset.sliderOptions || "{}") : {});

    // Helper to calculate and apply container alignment
    const applyContainerAlignment = (options, mainEl, sliderId) => {
        const shouldAlign = options.alignWithContainer === true;

        if (!shouldAlign) {
            return;
        }

        const calculateOffset = () => {
            const containerEl = document.querySelector(".container");
            const sliderEl = document.querySelector(
                `#${sliderId} ${mainEl.classList.contains("swiper") ? ".swiper" : "." + Array.from(mainEl.classList).find((c) => c.includes("slider") || c.includes("carousel") || c === mainEl.className)}`,
            );

            if (containerEl && sliderEl) {
                const containerRect = containerEl.getBoundingClientRect();
                const sliderRect = sliderEl.getBoundingClientRect();

                // Get container's left padding
                const containerPaddingLeft = parseFloat(window.getComputedStyle(containerEl).paddingLeft) || 0;

                const containerOffset = containerRect.left + window.scrollX + containerPaddingLeft;
                const sliderOffset = sliderRect.left + window.scrollX;
                const offsetValue = Math.max(0, containerOffset - sliderOffset);

                if (offsetValue > 0) {
                    options.slidesOffsetBefore = offsetValue;
                    options.slidesOffsetAfter = offsetValue;
                }
            }
        };

        // Calculate initial offset
        calculateOffset();

        // Recalculate on resize. Returned so a re-init can drop it -- this used
        // to be added on every init and never removed.
        window.addEventListener("resize", calculateOffset);

        return () => window.removeEventListener("resize", calculateOffset);
    };

    // Helper to assign unique navigation button classes and update options
    const assignNavButtons = (sliderId, options, type = "main") => {
        ["prev", "next"].forEach((btn) => {
            const selector =
                type === "main"
                    ? `#${sliderId} .swiper-button-${btn}.main-button, #${sliderId} .swiper-button-${btn}`
                    : `#${sliderId} .swiper-button-${btn}.thumbnail-button`;
            const button = document.querySelector(selector);
            if (button) {
                const btnClass = `${sliderId}-${type}-${btn}`;
                button.classList.add(btnClass);
                options.navigation = options.navigation || {};
                options.navigation[`${btn}El`] = `.${btnClass}`;
            }
        });
    };

    // Helper to handle reduced motion settings
    const applyReducedMotion = (options, reduced) => {
        if (reduced) {
            options.autoplay = false;
            options.speed = 0;
        }
    };

    const measureAutoPerView = (containerEl, slide) => {
        const slideWidth = slide?.getBoundingClientRect().width || 0;
        const containerWidth = containerEl.getBoundingClientRect().width || 0;

        return slideWidth > 0 ? Math.ceil(containerWidth / slideWidth) : 0;
    };

    const greatestCommonDivisor = (a, b) => (b === 0 ? a : greatestCommonDivisor(b, a % b));

    // How many full copies of the slide set the loop needs. Copies are whole sets so
    // that every real slide keeps the same number of stand-ins, and the total has to
    // stay a multiple of slidesPerGroup: with loopAddBlankSlides off, Swiper has no
    // filler left to even out a last, short group. 3 slides at slidesPerGroup 4 needs
    // 4 sets (12), not the 3 that merely clear slidesPerView * 2.
    const setsNeededForLoop = (slideCount, perView, group) => {
        let required = Math.ceil(perView) * 2;
        required += (group - (required % group)) % group;

        const step = group / greatestCommonDivisor(slideCount, group);
        const sets = Math.max(1, Math.ceil(required / slideCount));

        return Math.ceil(sets / step) * step;
    };

    // Swiper 9+ moves the real slides around for loop mode instead of duplicating
    // them, so it needs `slides >= slidesPerView * 2`, in a multiple of
    // slidesPerGroup. Below that the loop half-works: the translate lands on a snap
    // where slides are missing and the tail stays empty until you swipe past the
    // point where loopFix can rearrange. Swiper's own warning says to add duplicates,
    // so that is what happens here -- the loop the client asked for stays on.
    const prepareLoop = (containerEl, options) => {
        if (!options.loop || !containerEl) return null;

        const wrapper = containerEl.querySelector(".swiper-wrapper");
        if (!wrapper) return null;

        wrapper.querySelectorAll("[data-soil-clone]").forEach((node) => node.remove());

        const originals = Array.from(wrapper.children);
        if (!originals.length) return null;

        // Cards render video embeds as autoplaying iframes. A clone is a second
        // player of the same video, so those sliders drop the loop instead.
        if (originals.some((slide) => slide.querySelector("iframe, video"))) {
            options.loop = false;

            return null;
        }

        // loop is not overridable per breakpoint, so the widest breakpoint decides.
        const perView = [
            options.slidesPerView,
            ...Object.values(options.breakpoints || {}).map((bp) => bp.slidesPerView),
        ];
        const numeric = perView.map((value) => parseFloat(value)).filter((value) => !isNaN(value));
        const isAuto = perView.includes("auto");
        const group = Math.max(1, parseInt(options.slidesPerGroup, 10) || 1);

        // Swiper's filler slides are what render as the empty gaps; the clones below
        // make the count come out even, so they are never wanted.
        options.loopAddBlankSlides = false;

        let sets = 1;

        const fill = () => {
            // An `auto` breakpoint only shows how many slides fit once it is the
            // active one, so this is measured again on resize and tops up from there.
            const measured = isAuto ? measureAutoPerView(containerEl, originals[0]) : 0;
            const maxPerView = Math.max(1, Math.max(measured, ...numeric, 0));
            const needed = setsNeededForLoop(originals.length, maxPerView, group);

            if (needed <= sets) return false;

            const fragment = document.createDocumentFragment();

            for (let copy = sets; copy < needed; copy++) {
                originals.forEach((slide) => {
                    const clone = slide.cloneNode(true);
                    clone.dataset.soilClone = "1";
                    clone.setAttribute("aria-hidden", "true");
                    clone.inert = true;
                    clone.removeAttribute("id");
                    clone.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"));
                    fragment.appendChild(clone);
                });
            }

            wrapper.appendChild(fragment);
            sets = needed;

            // Anything that bound handlers per element before these existed -- the
            // favourites buttons do, from a bundle that loads earlier -- can wire the
            // copies up here.
            containerEl.dispatchEvent(
                new CustomEvent("wpsoup/slider/slides-cloned", {
                    bubbles: true,
                    detail: { container: containerEl, clones: [...wrapper.querySelectorAll("[data-soil-clone]")] },
                }),
            );

            return true;
        };

        const cloned = fill();
        let loopFillHandle = null;

        const needsMoreSets = () => {
            const measured = isAuto ? measureAutoPerView(containerEl, originals[0]) : 0;
            const maxPerView = Math.max(1, Math.max(measured, ...numeric, 0));

            return setsNeededForLoop(originals.length, maxPerView, group) > sets;
        };

        // An `auto` slider that needs no clones at this width may still need them at a
        // wider breakpoint, so it keeps the handle for the resize top-up.
        if (!cloned && !isAuto) return null;

        loopFillHandle = {
            originalCount: originals.length,
            group,
            isAuto,
            cloned,
            needsMoreSets,
            restore: () => wrapper.querySelectorAll("[data-soil-clone]").forEach((node) => node.remove()),
        };

        return loopFillHandle;
    };

    const paginationElements = (instance) => {
        const element = instance.pagination?.el;

        return element ? (Array.isArray(element) ? element : [element]) : [];
    };

    // Swiper's rolling bullet window runs over every bullet it rendered, clones
    // included, on a strip it clips to the window's width. Once the clones' bullets are
    // hidden, that math measures against bullets that are no longer there and pushes
    // the active one outside the strip: dots on screen, none of them lit. This is the
    // same window over the real bullets only, following pagination.mjs: outer bullet
    // size, a window of dynamicMainBullets + 4, and the offset written on each bullet's
    // `left` rather than on the strip.
    const renderDynamicWindow = (instance, bullets, page) => {
        if (!bullets.length) return;

        const params = instance.params.pagination;
        const activeClass = params.bulletActiveClass;
        const mainBullets = params.dynamicMainBullets || 1;
        const horizontal = instance.isHorizontal();
        const style = window.getComputedStyle(bullets[0], null);
        const bulletSize = horizontal
            ? bullets[0].offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight)
            : bullets[0].offsetHeight + parseFloat(style.marginTop) + parseFloat(style.marginBottom);

        if (!bulletSize) return;

        // Mirrors Swiper's own drift of the active bullet inside the main group; with
        // the default of one main bullet it stays put.
        if (mainBullets > 1) {
            dynamicBulletIndex += page - previousPage;
            dynamicBulletIndex = Math.min(Math.max(dynamicBulletIndex, 0), mainBullets - 1);
        }

        previousPage = page;

        const firstIndex = Math.max(page - dynamicBulletIndex, 0);
        const lastIndex = firstIndex + (Math.min(bullets.length, mainBullets) - 1);
        const midIndex = (firstIndex + lastIndex) / 2;
        const windowLength = Math.min(bullets.length, mainBullets + 4);
        const offset = (bulletSize * windowLength - bulletSize) / 2 - midIndex * bulletSize;

        const sideClasses = ["-next", "-next-next", "-prev", "-prev-prev", "-main"].map(
            (suffix) => `${activeClass}${suffix}`,
        );

        bullets.forEach((bullet) => bullet.classList.remove(...sideClasses));

        for (let index = firstIndex; index <= lastIndex; index += 1) {
            bullets[index]?.classList.add(`${activeClass}-main`);
        }

        setSideBullet(bullets[firstIndex], "prev", activeClass);
        setSideBullet(bullets[lastIndex], "next", activeClass);

        const positionProperty = horizontal ? "left" : "top";

        bullets.forEach((bullet) => {
            bullet.style[positionProperty] = `${offset}px`;
        });

        paginationElements(instance).forEach((element) => {
            element.style[horizontal ? "width" : "height"] = `${bulletSize * windowLength}px`;
        });
    };

    // The two shrinking bullets on either side of the main group.
    const setSideBullet = (bullet, position, activeClass) => {
        if (!bullet) return;

        const sibling = position === "prev" ? "previousElementSibling" : "nextElementSibling";
        const neighbour = bullet[sibling];
        if (!neighbour) return;

        neighbour.classList.add(`${activeClass}-${position}`);
        neighbour[sibling]?.classList.add(`${activeClass}-${position}-${position}`);
    };

    // Pagination counts the DOM slides, clones included, so every type has to be told
    // what the real slide set is. A page is slidesPerGroup slides wide, and realIndex
    // is the only index that still points at a real slide once loopFix has shuffled
    // the DOM around.
    const syncClonedPagination = (instance, fill) => {
        const pages = Math.ceil(fill.originalCount / fill.group);

        const update = () => {
            const pagination = instance.pagination;
            const [paginationEl] = paginationElements(instance);
            if (!paginationEl) return;

            const page = Math.floor((instance.realIndex % fill.originalCount) / fill.group);
            const type = instance.params.pagination.type;

            if (type === "bullets" && pagination.bullets?.length) {
                const activeClass = instance.params.pagination.bulletActiveClass;

                pagination.bullets.forEach((bullet, index) => {
                    bullet.style.display = index >= pages ? "none" : "";
                    bullet.classList.toggle(activeClass, index === page);
                });

                if (instance.params.pagination.dynamicBullets) {
                    renderDynamicWindow(instance, pagination.bullets.slice(0, pages), page);
                }

                return;
            }

            if (type === "fraction") {
                const current = paginationEl.querySelector(`.${instance.params.pagination.currentClass}`);
                const total = paginationEl.querySelector(`.${instance.params.pagination.totalClass}`);

                if (current) current.textContent = String(page + 1);
                if (total) total.textContent = String(pages);

                return;
            }

            if (type === "progressbar") {
                const fillEl = paginationEl.querySelector(`.${instance.params.pagination.progressbarFillClass}`);

                if (fillEl) {
                    const scale = pages > 0 ? (page + 1) / pages : 1;
                    fillEl.style.transform = `translate3d(0px,0px,0px) scaleX(${scale}) scaleY(1)`;
                }
            }
        };

        instance.on("paginationRender paginationUpdate slideChange transitionEnd", update);
        update();

        const elements = paginationElements(instance);
        const bulletClass = (instance.params.pagination.bulletClass || "swiper-pagination-bullet").split(" ")[0];

        return () => {
            // The window writes a width on the strip and a `left` on every bullet.
            // Swiper only rewrites those while it is in dynamic mode itself, so a
            // rebuild into any other pagination inherits a strip clipped to five
            // bullets unless they are cleared here.
            // Read from the DOM, not from instance.pagination: destroy() runs first
            // and empties the module.
            elements.forEach((element) => {
                element.style.width = "";
                element.style.height = "";

                element.querySelectorAll(`.${bulletClass}`).forEach((bullet) => {
                    bullet.style.display = "";
                    bullet.style.left = "";
                    bullet.style.top = "";
                });
            });
        };
    };

    // Clones put every card in the DOM more than once, and loopFix shuffles which copy
    // is on screen, so "is this a clone" is the wrong question -- "is this on screen"
    // is the right one. Off-screen slides, original or clone, are taken out of the tab
    // order and hidden from assistive tech; the visible copy keeps both.
    const syncSlideVisibility = (instance) => {
        const update = () => {
            instance.slides.forEach((slide) => {
                const visible = slide.classList.contains("swiper-slide-visible");

                if (visible) {
                    slide.inert = false;
                    slide.removeAttribute("aria-hidden");

                    return;
                }

                // Pulling the focused element out of the tab order would drop focus to
                // the body mid-interaction; it is hidden on the next pass instead.
                if (slide.contains(document.activeElement)) return;

                slide.inert = true;
                slide.setAttribute("aria-hidden", "true");
            });
        };

        instance.on("slideChange transitionEnd resize update", update);

        // Focus moving out of a skipped slide is not a Swiper event, so without this
        // the slide it left stays focusable until the next slide change -- long enough
        // to shift-tab straight back into it. Deferred, because focusout fires before
        // document.activeElement points at the new target.
        let focusOutTimeout = null;

        const onFocusOut = () => {
            clearTimeout(focusOutTimeout);
            focusOutTimeout = setTimeout(update, 0);
        };

        // Held onto separately: destroy() clears instance.el, and the teardown below
        // runs after it.
        const element = instance.el;

        element.addEventListener("focusout", onFocusOut);
        update();

        return () => {
            clearTimeout(focusOutTimeout);
            element.removeEventListener("focusout", onFocusOut);

            // A destroyed slider is plain markup again: leaving slides inert would
            // keep real cards out of the tab order with nothing left to restore them.
            element.querySelectorAll(".swiper-slide").forEach((slide) => {
                slide.inert = false;
                slide.removeAttribute("aria-hidden");
            });
        };
    };

    // Helper to (re)initialize Swiper instances
    const reinitSlider = (selector, options, onSlideChange) => {
        const instance = new Swiper(selector, options);
        if (onSlideChange) instance.on("slideChange", onSlideChange);
        return instance;
    };

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const hasThumb = sliders.thumb != null;

    // The thumb path has always hardcoded `.carousel` for the main slider while
    // the plain path honours `sliders.selector`. Kept as it is: changing it
    // would move which element a thumbed block initializes.
    const mainSelector = hasThumb
        ? `#${sliders.id} .carousel`
        : sliders?.selector
          ? `#${sliders.id} .${sliders.selector}`
          : `#${sliders.id} .carousel`;
    const thumbSelector = `#${sliders.id} .thumbnail-carousel`;

    const mainOptions = parseOptions(sliders.main);
    assignNavButtons(sliders.id, mainOptions, "main");
    const stopAlignment = applyContainerAlignment(mainOptions, sliders.main, sliders.id);

    // Snapshots taken BEFORE reduced motion is applied, so every build starts
    // from the same pristine options rather than from a mutated set.
    const originalMainOptions = JSON.parse(JSON.stringify(mainOptions));

    let originalThumbOptions = null;
    if (hasThumb) {
        const thumbOptions = parseOptions(sliders.thumb);
        assignNavButtons(sliders.id, thumbOptions, "thumb");
        originalThumbOptions = JSON.parse(JSON.stringify(thumbOptions));
    }

    let mainSlider = null;
    let thumbSlider = null;
    let loopFill = null;
    let loopFillActive = false;
    let stopVisibilitySync = null;
    let stopPaginationSync = null;
    let dynamicBulletIndex = 0;
    let previousPage = 0;
    let loopResizeTimeout = null;

    // Applied to the live instance rather than to the init options, because an `auto`
    // slider only finds out that it needs clones when the viewport grows.
    const activateLoopFill = () => {
        if (!mainSlider || loopFillActive) return;

        loopFillActive = true;

        // syncSlideVisibility decides what is focusable from `swiper-slide-visible`,
        // and Swiper only maintains that class while it watches slide progress.
        mainSlider.params.watchSlidesProgress = true;

        mainSlider.update();

        stopPaginationSync = syncClonedPagination(mainSlider, loopFill);
        stopVisibilitySync = syncSlideVisibility(mainSlider);
    };

    // Appending into a running loop is not enough: Swiper indexes its slides when it
    // creates the loop, and loopDestroy() sorts on that index, so extra copies carrying
    // a duplicate index would collapse the order. Swiper rebuilds its own loop on a
    // breakpoint change for the same reason, so this rebuilds too, through the ordinary
    // teardown and build.
    const onLoopResize = () => {
        clearTimeout(loopResizeTimeout);
        loopResizeTimeout = setTimeout(() => {
            if (!loopFill?.needsMoreSets()) return;

            const startIndex = mainSlider?.realIndex ?? null;

            destroySliders();
            build(mediaQuery.matches, startIndex);
        }, 200);
    };

    function slideChange() {
        const currentSlide = mainSlider.slides[mainSlider.activeIndex];
        if (!currentSlide) return;
        if (currentSlide.classList.contains("youtube")) {
            const iframe = currentSlide.querySelector("iframe");
            // Uncomment to play video:
            // if (iframe) iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        } else {
            mainSlider.slides.forEach((slide) => {
                const iframe = slide.querySelector("iframe");
                if (iframe) {
                    iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
                }
            });
        }
    }

    const build = (reduceMotion, startIndex = null) => {
        const main = JSON.parse(JSON.stringify(originalMainOptions));
        applyReducedMotion(main, reduceMotion);

        // prepareLoop reads the rendered slides, so it runs per build: a rebuild after
        // a motion change or a load-more starts from the current slide set.
        loopFill = prepareLoop(document.querySelector(mainSelector), main);

        if (!hasThumb) {
            mainSlider = reinitSlider(mainSelector, main);
        } else {
            const thumb = JSON.parse(JSON.stringify(originalThumbOptions));
            applyReducedMotion(thumb, reduceMotion);

            thumbSlider = reinitSlider(thumbSelector, thumb);
            main.thumbs = { swiper: thumbSlider };
            mainSlider = reinitSlider(mainSelector, main, slideChange);
        }

        if (loopFill?.cloned) {
            activateLoopFill();
        }

        // A rebuild otherwise drops the visitor back at the first slide.
        if (startIndex) {
            if (mainSlider.params.loop) {
                mainSlider.slideToLoop(startIndex, 0, false);
            } else {
                mainSlider.slideTo(startIndex, 0, false);
            }
        }

        // A breakpoint using `auto` can fit more slides than the one measured at init,
        // so the clone count is topped up when the viewport changes.
        if (loopFill?.isAuto) {
            window.addEventListener("resize", onLoopResize);
        }
    };

    const destroySliders = () => {
        mainSlider?.destroy();
        thumbSlider?.destroy();
        mainSlider = null;
        thumbSlider = null;

        // Without this the clones stack up on every rebuild, and each round would
        // clone the previous round's clones as well.
        window.removeEventListener("resize", onLoopResize);
        clearTimeout(loopResizeTimeout);
        stopVisibilitySync?.();
        stopVisibilitySync = null;
        stopPaginationSync?.();
        stopPaginationSync = null;
        dynamicBulletIndex = 0;
        previousPage = 0;
        loopFill?.restore();
        loopFill = null;
        loopFillActive = false;
    };

    const onMotionChange = () => {
        const startIndex = mainSlider?.realIndex ?? null;

        destroySliders();
        build(mediaQuery.matches, startIndex);
    };

    // Re-entry is real: DOMContentLoaded, the editor's MutationObserver, ACF's
    // render_block_preview and the exported window.initializeSliders all reach
    // here. Without this, each pass left its Swipers and its media-query
    // listener behind, so one motion change fired N handlers closed over
    // destroyed instances.
    // window.initializeSliders is public, so a theme may call this without a
    // main element; parseOptions already tolerated that.
    if (sliders.main) {
        sliders.main.soilSliderTeardown?.();
        sliders.main.soilSliderTeardown = () => {
            destroySliders();
            mediaQuery.removeEventListener("change", onMotionChange);
            stopAlignment?.();
        };
    }

    mediaQuery.addEventListener("change", onMotionChange);
    build(!mediaQuery || mediaQuery.matches);
};
window.initializeSliders = initializeSliders;

// Apply above function to all sliders.
function msInitializeDynamicSliders() {
    let listOfSliders = [];

    // Loop through each slider block type from config
    Object.entries(SLIDER_CONFIG).forEach(([containerSelector, config]) => {
        document.querySelectorAll(containerSelector).forEach((container) => {
            // Handle both single config and array of configs
            const configs = Array.isArray(config) ? config : [config];

            configs.forEach((cfg) => {
                const mainElement = container.querySelector(cfg.main);
                if (mainElement) {
                    listOfSliders.push({
                        id: container.id,
                        main: mainElement,
                        thumb: cfg.thumb ? container.querySelector(cfg.thumb) : null,
                        selector: cfg.selector,
                    });
                }
            });
        });
    });

    if (listOfSliders.length !== 0) {
        listOfSliders.forEach((sliders) => {
            initializeSliders(sliders);
        });
    }
}

document.addEventListener(
    `DOMContentLoaded`,
    function () {
        msInitializeDynamicSliders();
    },
    { passive: true },
);

// For WordPress block editor iframe support
if (typeof wp !== "undefined" && wp.hooks && typeof acf === "undefined") {
    // Use MutationObserver to detect when new slider elements are added to the iframe
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", setupMutationObserver);
    } else {
        setupMutationObserver();
    }

    function setupMutationObserver() {
        let mutationTimeout;
        let isReinitializing = false;

        const observer = new MutationObserver(() => {
            // Skip if we're already reinitializing
            if (isReinitializing) {
                return;
            }

            // Debounce to prevent infinite loop from DOM modifications during initialization
            clearTimeout(mutationTimeout);
            mutationTimeout = setTimeout(() => {
                // Check if any swiper element doesn't have the swiper-initialized class
                const swiperSelector = SLIDER_SELECTORS.swipers
                    .split(", ")
                    .map((sel) => `${sel}:not(.swiper-initialized)`)
                    .join(", ");
                const uninitializedSwiper = document.querySelector(swiperSelector);

                if (uninitializedSwiper) {
                    isReinitializing = true;
                    msInitializeDynamicSliders();
                    // Reset flag after initialization completes
                    setTimeout(() => {
                        isReinitializing = false;
                    }, 500);
                }
            }, 300); // Wait 300ms after mutations stop before reinitializing
        });

        observer.observe(document.body || document.documentElement, {
            childList: true,
            subtree: true,
            attributes: false,
            characterData: false,
        });
    }
}

if (typeof acf !== "undefined") {
    // ACF hook for non-iframed editors
    acf.addAction(`render_block_preview`, function ($el, attributes) {
        msInitializeDynamicSliders();
    });
}

$(".swiper-button-next, .swiper-button-prev").on("mousedown", function (e) {
    e.preventDefault();
});
